'use client';

import { FEATURE_STATS_ENABLED, FEATURE_TESTIMONIOS_ENABLED } from '@/lib/features';
import { AppImage, EmailIcon, InstagramIcon, WhatsAppIcon } from '@impacta/shared';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import ContactForm from '@/components/forms/ContactForm';

// ─── Types ───────────────────────────────────────────────────────────────────
interface TeamMember {
  name: string;
  role: string;
  bio: string;
  image: string;
  alt: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  avatar: string;
  alt: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const teamMembers: TeamMember[] = [
  {
    name: 'Adolfo Barraza',
    role: 'Fundador & Director',
    bio: 'Médico apasionado por la docencia y el desarrollo del mundo interior. Investigador continuo del bienestar y autocuidado. Más de veinte años dedicado a la docencia de adultos y realizar actividades en múltiples disciplinas.',
    image: '/assets/images/nosotros/team-adolfo.jpg',
    alt: 'Adolfo Barraza, fundador de IMPACTA.red',
  },
  {
    name: 'Valentina Verdugo',
    role: 'Gestora Cultural',
    bio: 'Magíster en gestión cultural (U. de Chile) y artista visual (U. Diego Portales). Especialista en mediación artística, turismo patrimonial (Japón, Australia, Nueva Zelanda y Chile) y coordinación de equipos multidisciplinarios. Con el propósito de compartir conocimientos y promover la colaboración intercultural.',
    image: '/assets/images/nosotros/team-valentina.jpg',
    alt: 'N/A',
  },
  {
    name: 'Esteban Campos',
    role: 'Constructor de plataformas digitales',
    bio: 'Ingeniero en informática (U. de Santiago) residente en Melbourne, Australia. Crea herramientas digitales funcionales para empresas y personas. Pilar de la interfaz entre talleristas y participantes. ',
    image: '/assets/images/nosotros/team-esteban.jpg',
    alt: 'N/A',
  },
  {
    name: 'Asesores y Consultores',
    role: 'Múltiples Profesionales',
    bio: 'Profesionales de excelencia en múltiples ámbitos que han permitido construir una comunidad y empresa sólida centrada en las personas (Abogadas y abogados, Contadores, Diseñadores, Asesores financieros y Especialistas en OTEC)',
    image: '/assets/images/nosotros/team-community.png',
    alt: 'N/A',
  },
];

const testimonials: Testimonial[] = [
  {
    quote:
      'IMPACTA.red cambió mi forma de ver el aprendizaje. Encontré una actividad de cerámica que nunca hubiera descubierto sola, y ahora es parte de mi vida.',
    author: 'Andrea Castillo',
    role: 'Estudiante de cerámica',
    avatar: '/assets/images/nosotros/testimonial-andrea.jpg',
    alt: 'Andrea Castillo, estudiante de cerámica, sonriendo',
  },
  {
    quote:
      'La plataforma es increíblemente fácil de usar. En minutos encontré la actividad perfecto y la experiencia superó todas mis expectativas.',
    author: 'Roberto Sánchez',
    role: 'Participante de taller de fotografía',
    avatar: '/assets/images/nosotros/testimonial-roberto.png',
    alt: 'Roberto Sánchez, participante de taller de fotografía, con cámara en mano',
  },
  {
    quote:
      'Regalé una experiencia de cocina a mi pareja y fue el mejor regalo que he dado. El equipo de IMPACTA.red hizo todo muy sencillo y especial.',
    author: 'Francisca Herrera',
    role: 'Cliente de regalo experiencial',
    avatar: '/assets/images/nosotros/testimonial-francisca.png',
    alt: 'Francisca Herrera, cliente de regalo experiencial, feliz con su compra',
  },
  {
    quote:
      'Como tallerista, IMPACTA.red me dio visibilidad que nunca tuve antes. Mis clases están llenas y la comunidad que se ha formado es extraordinaria.',
    author: 'Diego Muñoz',
    role: 'Tallerista de música',
    avatar: '/assets/images/nosotros/testimonial-diego.png',
    alt: 'Diego Muñoz, tallerista de música, tocando guitarra en su taller',
  },
];

// ─── Fade-in Hook ─────────────────────────────────────────────────────────────
function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function NosotrosClient() {
  const [teamIndex, setTeamIndex] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const heroFade = useFadeIn();
  const misionFade = useFadeIn();
  const equipoFade = useFadeIn();
  const ctaFade = useFadeIn();
  const testimoniosFade = useFadeIn();
  const contactoFade = useFadeIn();

  const visibleTeam = teamMembers.slice(teamIndex, teamIndex + 3);
  const visibleTestimonials = testimonials.slice(testimonialIndex, testimonialIndex + 2);

  const nextTeam = () => setTeamIndex((prev) => (prev + 1) % teamMembers.length);
  const prevTeam = () =>
    setTeamIndex((prev) => (prev - 1 + teamMembers.length) % teamMembers.length);
  const nextTestimonial = () => setTestimonialIndex((prev) => (prev + 2) % testimonials.length);
  const prevTestimonial = () =>
    setTestimonialIndex((prev) => (prev - 2 + testimonials.length) % testimonials.length);

  return (
    <div className="bg-white">
      {/* ── 1. MINI HERO ─────────────────────────────────────────────────── */}
      <section
        ref={heroFade.ref}
        className={`relative overflow-hidden bg-[#F7F7F5] transition-all duration-700 ${
          heroFade.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        {/* Subtle background accent */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#00A19B]/5 rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#E0DF00]/8 rounded-full -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 py-24 lg:py-32 text-center">
          <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00A19B] mb-4">
            Nuestra Historia
          </span>
          <h1 className="text-4xl lg:text-6xl font-bold text-[#373A36] leading-tight mb-6">
            Bienvenidos a la <span className="text-[#00A19B]">Construcción de tu experiencia</span>
          </h1>
          <p className="text-lg lg:text-xl text-[#6b6e68] max-w-2xl mx-auto leading-relaxed">
            Somos IMPACTA.red, una interfaz que conecta personas con expertos en experiencias
            aditivas sensoriales-cognitivas significativas. Creemos en los procesos que transforman
            vidas y construyen comunidad.
          </p>
          <div className="mt-10 flex items-center justify-center gap-2">
            <div className="w-8 h-0.5 bg-[#00A19B]" />
            <div className="w-2 h-2 rounded-full bg-[#00A19B]" />
            <div className="w-8 h-0.5 bg-[#00A19B]" />
          </div>
        </div>
      </section>

      {/* ── 2. NUESTRA MISIÓN ────────────────────────────────────────────── */}
      <section
        ref={misionFade.ref}
        className={`py-20 lg:py-28 transition-all duration-700 delay-100 ${
          misionFade.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text */}
            <div>
              <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00A19B] mb-3">
                Quiénes somos
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#373A36] mb-6 leading-tight">
                Nuestra Misión
              </h2>
              <p className="text-[#6b6e68] text-lg leading-relaxed mb-5">
                En IMPACTA.red creemos que el aprendizaje más poderoso ocurre cuando las personas se
                reúnen, inspiran mutuamente y crean. Nuestra misión es hacer accesible ese tipo de
                vivencias para todas y todos.
              </p>
              <p className="text-[#6b6e68] leading-relaxed mb-5">
                Abarcamos arte, bienestar, cocina, fotografía, comunicación, danza, salud, música,
                narrativa, naturaleza, mundo digital e innovaciones. Cada actividad en nuestra
                plataforma es verificada y curada para propender una experiencia de calidad.
              </p>
              <p className="text-[#6b6e68] leading-relaxed">
                Trabajamos con talleristas apasionados que comparten con autenticidad sus
                habilidades y saberes; creando espacios que se transforman en una experiencia
                recordable.
              </p>
              {FEATURE_STATS_ENABLED && (
                <div className="mt-8 grid grid-cols-3 gap-6">
                  {[
                    { num: '500+', label: 'Actividades curadas' },
                    { num: '8.000+', label: 'Estudiantes' },
                    { num: '200+', label: 'Talleristas' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-2xl font-bold text-[#00A19B]">{stat.num}</div>
                      <div className="text-xs text-[#6b6e68] mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Image */}
            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden">
                <AppImage
                  src="/assets/images/nosotros/mision.jpg"
                  alt="Grupo de personas aprendiendo juntas en una actividad presencial de IMPACTA.red en Santiago"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
              </div>
              {/* Accent card */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg p-4 border border-[#E8E8E4]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E0DF00] flex items-center justify-center text-lg">
                    ✨
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#373A36]">Experiencias reales</div>
                    <div className="text-xs text-[#6b6e68]">Presenciales en Santiago</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. CONOCE A NUESTRO EQUIPO ───────────────────────────────────── */}
      <section
        ref={equipoFade.ref}
        className={`py-20 lg:py-28 bg-[#F7F7F5] transition-all duration-700 delay-100 ${
          equipoFade.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00A19B] mb-3">
              Las personas detrás
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#373A36]">
              Conoce a Nuestro Equipo
            </h2>
          </div>
          {/* Carousel */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {visibleTeam.map((member) => (
                <div
                  key={member.name}
                  className="bg-white rounded-2xl overflow-hidden border border-[#E8E8E4] hover:shadow-md transition-shadow duration-300"
                >
                  <div className="relative aspect-square overflow-hidden">
                    <AppImage
                      src={member.image}
                      alt={member.alt}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 33vw"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-[#373A36] text-lg">{member.name}</h3>
                    <p className="text-[#00A19B] text-sm font-semibold mb-3">{member.role}</p>
                    <p className="text-[#6b6e68] text-sm leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Nav buttons */}
            <div className="flex justify-center gap-3 mt-8">
              <button
                onClick={prevTeam}
                className="w-10 h-10 rounded-full border border-[#E8E8E4] bg-white flex items-center justify-center text-[#373A36] hover:border-[#00A19B] hover:text-[#00A19B] transition-colors"
                aria-label="Anterior"
              >
                ←
              </button>
              <button
                onClick={nextTeam}
                className="w-10 h-10 rounded-full border border-[#E8E8E4] bg-white flex items-center justify-center text-[#373A36] hover:border-[#00A19B] hover:text-[#00A19B] transition-colors"
                aria-label="Siguiente"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. ÚNETE A NOSOTROS ──────────────────────────────────────────── */}
      <section
        ref={ctaFade.ref}
        className={`py-20 lg:py-28 bg-[#373A36] transition-all duration-700 delay-100 ${
          ctaFade.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00A19B] mb-4">
            Sé parte
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Únete a Nosotros
          </h2>
          <p className="text-white/80 text-lg leading-relaxed mb-10">
            Sé parte de una comunidad que valora los aprendizajes aditivos y recordables. ¿Quieres
            vivir la experiencia de aprender e inspirarte en un área de nuestro catálogo de
            oportunidades? ¿Quieres compartir como tallerista tus intereses?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/activities"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-sm bg-[#E0DF00] text-[#373A36] hover:bg-[#00A19B] hover:text-white transition-all duration-200"
            >
              Explorar actividades
            </Link>
            <Link
              href="/postular"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full font-bold text-lg transition-all duration-200 bg-[#612166] text-white border-2 border-[rgba(192,132,204,0.3)] hover:bg-[#7d2b82] hover:scale-105 hover:shadow-2xl"
            >
              Ser tallerista
            </Link>
          </div>
        </div>
      </section>

      {/* ── 5. LO QUE DICEN DE NOSOTROS ─────────────────────────────────── */}
      {FEATURE_TESTIMONIOS_ENABLED && (
        <section
          ref={testimoniosFade.ref}
          className={`py-20 lg:py-28 transition-all duration-700 delay-100 ${
            testimoniosFade.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00A19B] mb-3">
                Testimonios
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#373A36]">
                Lo Que Dicen de Nosotros
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {visibleTestimonials.map((t) => (
                <div
                  key={t.author}
                  className="bg-[#F7F7F5] rounded-2xl p-8 border border-[#E8E8E4] flex flex-col"
                >
                  <div className="text-[#00A19B] text-3xl font-serif mb-4">&ldquo;</div>
                  <p className="text-[#373A36] leading-relaxed flex-1 text-base">{t.quote}</p>
                  <div className="flex items-center gap-4 mt-6 pt-6 border-t border-[#E8E8E4]">
                    <AppImage
                      src={t.avatar}
                      alt={t.alt}
                      width={48}
                      height={48}
                      className="w-12 h-12 rounded-full object-cover"
                    />

                    <div>
                      <div className="font-bold text-[#373A36] text-sm">{t.author}</div>
                      <div className="text-[#6b6e68] text-xs">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Nav */}
            <div className="flex justify-center gap-3 mt-8">
              <button
                onClick={prevTestimonial}
                className="w-10 h-10 rounded-full border border-[#E8E8E4] bg-white flex items-center justify-center text-[#373A36] hover:border-[#00A19B] hover:text-[#00A19B] transition-colors"
                aria-label="Anterior testimonio"
              >
                ←
              </button>
              <button
                onClick={nextTestimonial}
                className="w-10 h-10 rounded-full border border-[#E8E8E4] bg-white flex items-center justify-center text-[#373A36] hover:border-[#00A19B] hover:text-[#00A19B] transition-colors"
                aria-label="Siguiente testimonio"
              >
                →
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── 6. CONTÁCTANOS ───────────────────────────────────────────────── */}
      <section
        ref={contactoFade.ref}
        className={`py-20 lg:py-28 bg-[#F7F7F5] transition-all duration-700 delay-100 ${
          contactoFade.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00A19B] mb-3">
              Hablemos
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#373A36]">Contáctanos</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Form */}
            <div className="bg-white rounded-2xl p-8 border border-[#E8E8E4]">
              <ContactForm
                subjectMode="hidden"
                defaultSubject="contactanos"
                className="space-y-5"
                successHeading="¡Mensaje enviado!"
                successBody="Te responderemos pronto. Gracias por contactarnos."
                successLinkHref="/"
                successLinkLabel="Volver al inicio"
              />
            </div>
            {/* Logo + Info */}
            <div className="space-y-6 flex flex-col min-h-full">
              {/* Logo card */}
              <div
                className="relative rounded-[20px] overflow-hidden bg-white flex-1 flex items-center justify-center min-h-[320px]"
                style={{
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)',
                }}
              >
                {/* Soft teal circle bottom-right */}
                <div
                  className="absolute rounded-full bg-[#00A19B]"
                  style={{ width: 280, height: 280, right: -40, bottom: -80, opacity: 0.1 }}
                />
                {/* Teal outline circle top-left */}
                <div
                  className="absolute rounded-full border-2 border-[#00A19B]"
                  style={{ width: 180, height: 180, top: -60, left: -60, opacity: 0.18 }}
                />
                <AppImage
                  src="/assets/images/logo-turquesa.png"
                  alt="IMPACTA.red"
                  width={240}
                  height={120}
                  className="object-contain max-h-full relative z-10"
                />
              </div>

              {/* Contact chips */}
              <div
                className="rounded-[20px] bg-white p-2 grid grid-cols-3 gap-2"
                style={{
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)',
                }}
              >
                {[
                  {
                    key: 'Email',
                    value: 'info@impacta.red',
                    Icon: EmailIcon,
                    href: 'mailto:info@impacta.red',
                  },
                  {
                    key: 'Instagram',
                    value: '@Impactared_Oficial',
                    Icon: InstagramIcon,
                    href: 'https://instagram.com/impactared_oficial',
                  },
                  {
                    key: 'WhatsApp',
                    value: '+56 9 3208 7667',
                    Icon: WhatsAppIcon,
                    href: 'https://wa.me/56932087667',
                  },
                ].map(({ key, value, Icon, href }) => (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 rounded-[14px] transition-all duration-200 hover:bg-[#e6f7f6] hover:-translate-y-0.5"
                  >
                    <span className="inline-flex w-9 h-9 rounded-[10px] bg-[#e6f7f6] text-[#0B8A82] items-center justify-center mb-2.5">
                      <Icon size={18} />
                    </span>
                    <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-[#00A19B] mb-0.5">
                      {key}
                    </span>
                    <span className="block text-[13px] font-semibold text-[#373A36] break-all">
                      {value}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,161,155,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(0,161,155,0); }
        }
      `}</style>
    </div>
  );
}
